const EMAIL_EDITOR_PROFILE_KEY = 'email';
const EMAIL_EDITOR_PROFILE_NAME = 'Email Editor';

const EMAIL_EDITOR_MODES = Object.freeze({
  PLATFORM_TEMPLATE: 'PLATFORM_TEMPLATE',
  TENANT_TEMPLATE: 'TENANT_TEMPLATE',
  CRM_COMPOSE: 'CRM_COMPOSE',
  PLAYGROUND: 'PLAYGROUND',
});

const EMAIL_BUTTON_STYLES = Object.freeze(['primary', 'secondary', 'outline', 'text']);
const EMAIL_BUTTON_SIZES = Object.freeze(['small', 'medium', 'large']);
const EMAIL_BUTTON_ICONS = Object.freeze(['none', 'arrow-right', 'external-link', 'download', 'calendar', 'phone', 'email']);
const EMAIL_BUTTON_ICON_POSITIONS = Object.freeze(['before', 'after']);
const EMAIL_BUTTON_DESTINATION_TYPES = Object.freeze(['URL', 'VARIABLE', 'EMAIL', 'PHONE']);
const EMAIL_ALIGNMENTS = Object.freeze(['left', 'center', 'right']);
const EMAIL_WIDTHS = Object.freeze(['auto', 'full']);
const EMAIL_IMAGE_WIDTH_PRESETS = Object.freeze(['small', 'medium', 'large', 'full']);
const EMAIL_DIVIDER_STYLES = Object.freeze(['standard', 'subtle']);
const EMAIL_SPACINGS = Object.freeze(['small', 'medium', 'large']);
const EMAIL_CALLOUT_TONES = Object.freeze(['neutral', 'info', 'success', 'warning']);

const ALLOWED_NODE_TYPES = Object.freeze([
  'doc',
  'paragraph',
  'text',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'mergeVariable',
  'objectVariable',
  'templateVariable',
  'emailButton',
  'emailImage',
  'emailDivider',
  'emailSpacer',
  'emailCallout',
]);

const ALLOWED_MARK_TYPES = Object.freeze(['bold', 'italic', 'underline', 'link', 'textStyle']);
const ALLOWED_HEADING_LEVELS = Object.freeze([1, 2]);

const DEFAULT_EMAIL_EDITOR_VARIABLES = Object.freeze([
  { name: 'recipient.firstName', label: 'Recipient first name', group: 'Recipient', description: 'Recipient first name.', required: false },
  { name: 'recipient.email', label: 'Recipient email', group: 'Recipient', description: 'Recipient email address.', required: false },
  { name: 'business.name', label: 'Business name', group: 'Business', description: 'Business or listing display name.', required: false },
  { name: 'business.url', label: 'Business URL', group: 'Business', description: 'Public or portal business URL.', required: false },
  { name: 'claim.status', label: 'Claim status', group: 'Claims', description: 'Current claim status label.', required: false },
  { name: 'cta.label', label: 'CTA label', group: 'Action', description: 'Primary action label.', required: false },
  { name: 'cta.url', label: 'CTA URL', group: 'Action', description: 'Primary action URL.', required: false, destination: true },
  { name: 'tenant.name', label: 'Tenant name', group: 'Tenant', description: 'Tenant display name.', required: false },
  { name: 'tenant.supportEmail', label: 'Tenant support email', group: 'Tenant', description: 'Tenant support email.', required: false },
  { name: 'sender.name', label: 'Sender name', group: 'Sender', description: 'Sender display name.', required: false },
]);

const EMAIL_EDITOR_MODE_CONFIG = Object.freeze({
  [EMAIL_EDITOR_MODES.PLATFORM_TEMPLATE]: Object.freeze({
    enablePreviewAsTenant: true,
    enableDeveloperTools: true,
    allowImages: true,
    allowButtons: true,
    allowCustomJsonImport: false,
  }),
  [EMAIL_EDITOR_MODES.TENANT_TEMPLATE]: Object.freeze({
    enablePreviewAsTenant: false,
    enableDeveloperTools: false,
    allowImages: true,
    allowButtons: true,
    allowCustomJsonImport: false,
  }),
  [EMAIL_EDITOR_MODES.CRM_COMPOSE]: Object.freeze({
    enablePreviewAsTenant: false,
    enableDeveloperTools: false,
    allowImages: true,
    allowButtons: true,
    allowCustomJsonImport: false,
  }),
  [EMAIL_EDITOR_MODES.PLAYGROUND]: Object.freeze({
    enablePreviewAsTenant: true,
    enableDeveloperTools: true,
    allowImages: true,
    allowButtons: true,
    allowCustomJsonImport: true,
  }),
});

const DEFAULT_EMAIL_EDITOR_BODY_DOC = Object.freeze({
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [
        { type: 'text', text: 'Hi ' },
        { type: 'mergeVariable', attrs: { name: 'recipient.firstName' } },
        { type: 'text', text: ',' },
      ],
    },
    {
      type: 'paragraph',
      content: [
        { type: 'text', text: 'Your claim for ' },
        { type: 'mergeVariable', attrs: { name: 'business.name' } },
        { type: 'text', text: ' has been approved. You can now manage your listing, update business details, publish offers, and review customer activity from your tenant portal.' },
      ],
    },
    {
      type: 'bulletList',
      content: [
        { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Review your public business information.' }] }] },
        { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Add photos, hours, offers, and contact details.' }] }] },
        { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Keep your listing fresh for local customers.' }] }] },
      ],
    },
    {
      type: 'emailCallout',
      attrs: { tone: 'info' },
      content: [
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: 'Tip: start with your highest-impact fields so customers see fresh information right away.' },
          ],
        },
      ],
    },
    {
      type: 'emailButton',
      attrs: {
        label: '{{cta.label}}',
        destinationType: 'VARIABLE',
        href: '',
        hrefVariable: 'cta.url',
        style: 'primary',
        alignment: 'left',
        width: 'auto',
        size: 'medium',
        icon: 'arrow-right',
        iconPosition: 'after',
        accessibleLabel: '',
        trackingLabel: 'claim-approved-primary-cta',
      },
    },
    {
      type: 'emailDivider',
      attrs: { style: 'subtle', spacing: 'medium' },
    },
    {
      type: 'paragraph',
      content: [
        { type: 'text', text: 'Thanks for keeping ' },
        { type: 'mergeVariable', attrs: { name: 'tenant.name' } },
        { type: 'text', text: ' accurate for the local community.' },
      ],
    },
  ],
});

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeString(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getPathValue(data, path) {
  return String(path || '').split('.').reduce((current, part) => (
    current && Object.prototype.hasOwnProperty.call(current, part) ? current[part] : undefined
  ), data);
}

function replaceVariables(text, data = {}) {
  return String(text || '').replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_match, name) => {
    const value = getPathValue(data, name);
    return value === undefined || value === null ? '' : String(value);
  });
}

function normalizeVariableOption(variable) {
  if (typeof variable === 'string') {
    return { name: variable, label: variable, group: 'Custom', description: '', required: false };
  }
  const name = normalizeString(variable?.name || variable?.key || variable?.path);
  return {
    name,
    label: normalizeString(variable?.label) || name,
    group: normalizeString(variable?.group || variable?.category) || 'Custom',
    description: normalizeString(variable?.description),
    required: variable?.required === true,
    destination: variable?.destination === true || /url$/i.test(name),
  };
}

function normalizeVariables(variables) {
  const source = Array.isArray(variables) && variables.length > 0 ? variables : DEFAULT_EMAIL_EDITOR_VARIABLES;
  const seen = new Set();
  return source.map(normalizeVariableOption).filter((variable) => {
    if (!variable.name || seen.has(variable.name)) return false;
    seen.add(variable.name);
    return true;
  });
}

function variableNames(variables) {
  return new Set(normalizeVariables(variables).map((variable) => variable.name));
}

function pathToString(path) {
  return path.length ? path.join('.') : 'doc';
}

function issue(code, severity, path, message) {
  return { code, severity, nodePath: pathToString(path), message };
}

function isSafeUrl(value, { allowVariables = false, approvedVariables = new Set() } = {}) {
  const url = normalizeString(value);
  if (!url) return false;
  const variableMatch = url.match(/^\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}$/);
  if (variableMatch) return allowVariables && approvedVariables.has(variableMatch[1]);
  if (/^(javascript|data|vbscript):/i.test(url)) return false;
  if (/^mailto:[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(url)) return true;
  if (/^tel:\+?[0-9().\-\s]+$/i.test(url)) return true;
  try {
    const parsed = new URL(url);
    return ['https:', 'http:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

function isSafeHttpUrl(value, { allowVariables = false, approvedVariables = new Set() } = {}) {
  const url = normalizeString(value);
  if (!url) return false;
  const variableMatch = url.match(/^\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}$/);
  if (variableMatch) return allowVariables && approvedVariables.has(variableMatch[1]);
  if (/^(javascript|data|vbscript|mailto|tel):/i.test(url)) return false;
  try {
    const parsed = new URL(url);
    return ['https:', 'http:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

function normalizeEmailAddress(value) {
  return normalizeString(value).replace(/^mailto:/i, '').trim();
}

function isSafeEmailAddress(value) {
  const email = normalizeEmailAddress(value);
  return /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email);
}

function normalizePhoneNumber(value) {
  return normalizeString(value).replace(/^tel:/i, '').trim();
}

function isSafePhoneNumber(value) {
  const phone = normalizePhoneNumber(value);
  return /^\+?[0-9().\-\s]{7,32}$/.test(phone);
}

function extractSingleMergeVariable(value) {
  const match = normalizeString(value).match(/^\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}$/);
  return match ? match[1] : '';
}

function collectVariablesFromString(value, out = new Set()) {
  const pattern = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
  let match = pattern.exec(String(value || ''));
  while (match) {
    out.add(match[1]);
    match = pattern.exec(String(value || ''));
  }
  return out;
}

function destinationVariableNames(variables) {
  return new Set(normalizeVariables(variables).filter((variable) => variable.destination).map((variable) => variable.name));
}

function normalizeEnum(value, allowed, fallback, { uppercase = false } = {}) {
  const normalized = normalizeString(value);
  const candidate = uppercase ? normalized.toUpperCase() : normalized;
  return allowed.includes(candidate) ? candidate : fallback;
}

function inferEmailButtonDestinationType(attrs = {}) {
  const explicit = normalizeEnum(attrs.destinationType, EMAIL_BUTTON_DESTINATION_TYPES, '', { uppercase: true });
  if (explicit) return explicit;
  if (normalizeString(attrs.hrefVariable) || extractSingleMergeVariable(attrs.href)) return 'VARIABLE';
  const href = normalizeString(attrs.href);
  if (/^mailto:/i.test(href) || isSafeEmailAddress(href)) return 'EMAIL';
  if (/^tel:/i.test(href)) return 'PHONE';
  return 'URL';
}

function normalizeTrackingLabel(value) {
  const label = normalizeString(value);
  return /^[a-zA-Z0-9_.:-]{1,80}$/.test(label) ? label : '';
}

function normalizeEmailButtonAttrs(attrs = {}) {
  const destinationType = inferEmailButtonDestinationType(attrs);
  const href = normalizeString(attrs.href);
  const hrefVariable = normalizeString(attrs.hrefVariable) || extractSingleMergeVariable(href);

  let normalizedHref = href;
  let normalizedHrefVariable = '';
  if (destinationType === 'VARIABLE') {
    normalizedHref = '';
    normalizedHrefVariable = hrefVariable;
  } else if (destinationType === 'EMAIL') {
    normalizedHref = normalizeEmailAddress(href);
  } else if (destinationType === 'PHONE') {
    normalizedHref = normalizePhoneNumber(href);
  }

  return {
    label: normalizeString(attrs.label) || 'Open',
    destinationType,
    href: normalizedHref,
    hrefVariable: normalizedHrefVariable,
    style: normalizeEnum(attrs.style, EMAIL_BUTTON_STYLES, 'primary'),
    alignment: normalizeEnum(attrs.alignment, EMAIL_ALIGNMENTS, 'left'),
    width: normalizeEnum(attrs.width, EMAIL_WIDTHS, 'auto'),
    size: normalizeEnum(attrs.size, EMAIL_BUTTON_SIZES, 'medium'),
    icon: normalizeEnum(attrs.icon, EMAIL_BUTTON_ICONS, 'none'),
    iconPosition: normalizeEnum(attrs.iconPosition, EMAIL_BUTTON_ICON_POSITIONS, 'after'),
    accessibleLabel: normalizeString(attrs.accessibleLabel).slice(0, 120),
    trackingLabel: normalizeTrackingLabel(attrs.trackingLabel),
  };
}

function validateMarks(node, issues, path) {
  if (!Array.isArray(node.marks)) return;
  node.marks.forEach((mark, index) => {
    const markPath = [...path, 'marks', String(index)];
    if (!ALLOWED_MARK_TYPES.includes(mark?.type)) {
      issues.push(issue('EMAIL_EDITOR_MARK_UNSUPPORTED', 'error', markPath, `Unsupported mark "${mark?.type || 'unknown'}".`));
      return;
    }
    if (mark.type === 'link' && !isSafeUrl(mark.attrs?.href)) {
      issues.push(issue('EMAIL_EDITOR_LINK_UNSAFE', 'error', markPath, 'Link URL must use http, https, mailto, or tel and cannot use unsafe schemes.'));
    }
  });
}

function validateNodeSpecifics(node, issues, path, variables, options = {}) {
  const approvedVariables = variableNames(variables);

  if (node.type === 'heading' && !(options.existingBody ? [1, 2, 3] : ALLOWED_HEADING_LEVELS).includes(Number(node.attrs?.level))) {
    issues.push(issue('EMAIL_EDITOR_HEADING_LEVEL_UNSUPPORTED', 'error', path, 'Email Editor supports Heading 1 and Heading 2 only.'));
  }

  if (node.type === 'mergeVariable' || node.type === 'objectVariable' || node.type === 'templateVariable') {
    const name = node.type === 'objectVariable'
      ? `${normalizeString(node.attrs?.objectKey)}.${normalizeString(node.attrs?.propertyKey)}`
      : node.type === 'templateVariable'
        ? normalizeString(node.attrs?.key || `${node.attrs?.object || ''}.${node.attrs?.property || ''}`)
        : normalizeString(node.attrs?.name || node.attrs?.key);
    if (!name) {
      issues.push(issue('EMAIL_EDITOR_VARIABLE_MISSING', 'error', path, 'Merge variable node requires a variable name.'));
    } else if (!approvedVariables.has(name)) {
      issues.push(issue('EMAIL_EDITOR_VARIABLE_UNKNOWN', 'error', path, `Merge variable "${name}" is not approved for this profile mode.`));
    }
  }

  if (node.type === 'emailButton') {
    const attrs = node.attrs || {};
    const label = normalizeString(attrs.label);
    const href = normalizeString(attrs.href);
    const hrefVariable = normalizeString(attrs.hrefVariable);
    const rawDestinationType = normalizeString(attrs.destinationType).toUpperCase();
    const destinationType = EMAIL_BUTTON_DESTINATION_TYPES.includes(rawDestinationType) ? rawDestinationType : inferEmailButtonDestinationType(attrs);
    const destinationVariables = destinationVariableNames(variables);
    const hrefTemplateVariable = extractSingleMergeVariable(href);
    if (!label) issues.push(issue('EMAIL_BUTTON_LABEL_MISSING', 'error', path, 'Email button requires a label.'));
    if (label.length > 80) issues.push(issue('EMAIL_BUTTON_LABEL_TOO_LONG', 'error', path, 'Email button label must be 80 characters or fewer.'));
    collectVariablesFromString(label).forEach((name) => {
      if (!approvedVariables.has(name)) issues.push(issue('EMAIL_BUTTON_LABEL_VARIABLE_UNKNOWN', 'error', path, `Email button label variable "${name}" is not approved.`));
    });
    if (attrs.destinationType && !EMAIL_BUTTON_DESTINATION_TYPES.includes(rawDestinationType)) issues.push(issue('EMAIL_BUTTON_DESTINATION_TYPE_INVALID', 'error', path, 'Email button destination type must be URL, VARIABLE, EMAIL, or PHONE.'));
    if (attrs.style && !EMAIL_BUTTON_STYLES.includes(attrs.style)) issues.push(issue('EMAIL_BUTTON_STYLE_INVALID', 'error', path, 'Email button style must be primary, secondary, outline, or text.'));
    if (attrs.alignment && !EMAIL_ALIGNMENTS.includes(attrs.alignment)) issues.push(issue('EMAIL_BUTTON_ALIGNMENT_INVALID', 'error', path, 'Email button alignment must be left, center, or right.'));
    if (attrs.width && !EMAIL_WIDTHS.includes(attrs.width)) issues.push(issue('EMAIL_BUTTON_WIDTH_INVALID', 'error', path, 'Email button width must be auto or full.'));
    if (attrs.size && !EMAIL_BUTTON_SIZES.includes(attrs.size)) issues.push(issue('EMAIL_BUTTON_SIZE_INVALID', 'error', path, 'Email button size must be small, medium, or large.'));
    if (attrs.icon && !EMAIL_BUTTON_ICONS.includes(attrs.icon)) issues.push(issue('EMAIL_BUTTON_ICON_INVALID', 'error', path, 'Email button icon must be one of the approved email-safe icons.'));
    if (attrs.iconPosition && !EMAIL_BUTTON_ICON_POSITIONS.includes(attrs.iconPosition)) issues.push(issue('EMAIL_BUTTON_ICON_POSITION_INVALID', 'error', path, 'Email button icon position must be before or after.'));
    if (normalizeString(attrs.accessibleLabel).length > 120) issues.push(issue('EMAIL_BUTTON_ACCESSIBLE_LABEL_TOO_LONG', 'error', path, 'Email button accessible label must be 120 characters or fewer.'));
    collectVariablesFromString(attrs.accessibleLabel).forEach((name) => {
      if (!approvedVariables.has(name)) issues.push(issue('EMAIL_BUTTON_ACCESSIBLE_LABEL_VARIABLE_UNKNOWN', 'error', path, `Email button accessibility label variable "${name}" is not approved.`));
    });
    if (normalizeString(attrs.trackingLabel) && !normalizeTrackingLabel(attrs.trackingLabel)) issues.push(issue('EMAIL_BUTTON_TRACKING_LABEL_INVALID', 'error', path, 'Email button tracking label may contain only letters, numbers, underscore, period, colon, and dash, up to 80 characters.'));

    if (destinationType === 'VARIABLE') {
      const variableName = hrefVariable || hrefTemplateVariable;
      if (!variableName) {
        issues.push(issue('EMAIL_BUTTON_DESTINATION_MISSING', 'error', path, 'Variable buttons require an approved destination merge variable.'));
      } else if (!destinationVariables.has(variableName)) {
        issues.push(issue('EMAIL_BUTTON_DESTINATION_VARIABLE_UNKNOWN', 'error', path, `Email button destination variable "${variableName}" is not approved for destinations.`));
      }
      if (href && !hrefTemplateVariable) issues.push(issue('EMAIL_BUTTON_DESTINATION_CONFLICT', 'error', path, 'Variable buttons must store the destination in hrefVariable, not href.'));
    } else if (destinationType === 'URL') {
      if (hrefVariable) issues.push(issue('EMAIL_BUTTON_DESTINATION_CONFLICT', 'error', path, 'URL buttons cannot also store a destination variable.'));
      if (!isSafeHttpUrl(href)) issues.push(issue('EMAIL_BUTTON_DESTINATION_INVALID', 'error', path, 'URL buttons require a safe http or https URL.'));
    } else if (destinationType === 'EMAIL') {
      if (hrefVariable) issues.push(issue('EMAIL_BUTTON_DESTINATION_CONFLICT', 'error', path, 'Email buttons cannot also store a destination variable.'));
      if (!isSafeEmailAddress(href)) issues.push(issue('EMAIL_BUTTON_DESTINATION_INVALID', 'error', path, 'Email buttons require a valid email address or mailto URL.'));
    } else if (destinationType === 'PHONE') {
      if (hrefVariable) issues.push(issue('EMAIL_BUTTON_DESTINATION_CONFLICT', 'error', path, 'Phone buttons cannot also store a destination variable.'));
      if (!isSafePhoneNumber(href)) issues.push(issue('EMAIL_BUTTON_DESTINATION_INVALID', 'error', path, 'Phone buttons require a valid phone number or tel URL.'));
    }
  }

  if (node.type === 'emailImage') {
    const attrs = node.attrs || {};
    const src = normalizeString(attrs.src);
    const alt = normalizeString(attrs.alt);
    const href = normalizeString(attrs.href);
    if (!src && !normalizeString(attrs.assetId)) issues.push(issue('EMAIL_IMAGE_SOURCE_MISSING', 'error', path, 'Email image requires an absolute URL or managed asset ID.'));
    if (src && !isSafeUrl(src)) issues.push(issue('EMAIL_IMAGE_SOURCE_UNSAFE', 'error', path, 'Email image source must use http or https.'));
    if (href && !isSafeUrl(href)) issues.push(issue('EMAIL_IMAGE_LINK_UNSAFE', 'error', path, 'Email image link must use a safe URL scheme.'));
    if (!alt) issues.push(issue('EMAIL_IMAGE_ALT_MISSING', 'warning', path, 'Email image should include alt text before publication.'));
    if (attrs.alignment && !EMAIL_ALIGNMENTS.includes(attrs.alignment)) issues.push(issue('EMAIL_IMAGE_ALIGNMENT_INVALID', 'error', path, 'Email image alignment must be left, center, or right.'));
    if (attrs.widthPreset && !EMAIL_IMAGE_WIDTH_PRESETS.includes(attrs.widthPreset)) issues.push(issue('EMAIL_IMAGE_WIDTH_INVALID', 'error', path, 'Email image width preset must be small, medium, large, or full.'));
  }

  if (node.type === 'emailDivider') {
    const attrs = node.attrs || {};
    if (attrs.style && !EMAIL_DIVIDER_STYLES.includes(attrs.style)) issues.push(issue('EMAIL_DIVIDER_STYLE_INVALID', 'error', path, 'Email divider style must be standard or subtle.'));
    if (attrs.spacing && !EMAIL_SPACINGS.includes(attrs.spacing)) issues.push(issue('EMAIL_DIVIDER_SPACING_INVALID', 'error', path, 'Email divider spacing must be small, medium, or large.'));
  }

  if (node.type === 'emailSpacer') {
    const size = node.attrs?.size || 'medium';
    if (!EMAIL_SPACINGS.includes(size)) issues.push(issue('EMAIL_SPACER_SIZE_INVALID', 'error', path, 'Email spacer size must be small, medium, or large.'));
  }

  if (node.type === 'emailCallout') {
    const tone = node.attrs?.tone || 'neutral';
    if (!EMAIL_CALLOUT_TONES.includes(tone)) issues.push(issue('EMAIL_CALLOUT_TONE_INVALID', 'error', path, 'Email callout tone must be neutral, info, success, or warning.'));
  }
}

function walkDocument(node, visitor, path = []) {
  if (!node || typeof node !== 'object') return;
  visitor(node, path);
  if (Array.isArray(node.content)) {
    node.content.forEach((child, index) => walkDocument(child, visitor, [...path, 'content', String(index)]));
  }
}

function validateEmailEditorDocument(document, options = {}) {
  const variables = normalizeVariables(options.allowedVariables);
  const issues = [];
  if (!document || document.type !== 'doc') {
    issues.push(issue('EMAIL_EDITOR_DOC_INVALID', 'error', [], 'Email Editor document must be a Tiptap doc.'));
    return issues;
  }

  const serializedLength = JSON.stringify(document).length;
  if (serializedLength > (options.maxDocumentBytes || 50000)) {
    issues.push(issue('EMAIL_EDITOR_DOC_TOO_LARGE', 'error', [], 'Email Editor document is too large.'));
  }

  walkDocument(document, (node, path) => {
    if (!ALLOWED_NODE_TYPES.includes(node.type) && !(options.existingBody && ['hardBreak', 'blockquote', 'horizontalRule', 'codeBlock', 'aiField', 'salesMaterial'].includes(node.type))) {
      issues.push(issue('EMAIL_EDITOR_NODE_UNSUPPORTED', 'error', path, `Unsupported node "${node.type || 'unknown'}".`));
    }
    if (node.type === 'salesMaterial' && (!node.attrs?.salesMaterialId || !['LINK', 'ATTACHMENT'].includes(node.attrs.deliveryMode || 'LINK'))) issues.push(issue('EMAIL_SALES_MATERIAL_INVALID', 'error', path, 'Sales material requires an identifier and supported delivery mode.'));
    if (node.type === 'aiField' && !(node.attrs?.outputKey || node.attrs?.fieldKey)) issues.push(issue('EMAIL_AI_FIELD_INVALID', 'error', path, 'AI field requires an output key.'));
    validateMarks(node, issues, path);
    const start = issues.length;
    validateNodeSpecifics(node, issues, path, variables, options);
    if (options.allowIncompleteDraft) for (let index = start; index < issues.length; index += 1) {
      const current = issues[index];
      if (['EMAIL_BUTTON_LABEL_MISSING', 'EMAIL_BUTTON_DESTINATION_MISSING', 'EMAIL_IMAGE_SOURCE_MISSING'].includes(current.code) || (current.code === 'EMAIL_BUTTON_DESTINATION_INVALID' && !normalizeString(node.attrs?.href))) current.severity = 'warning';
    }
  });

  return issues;
}

function collectEmailEditorVariables(document, out = new Set()) {
  walkDocument(document, (node) => {
    if (node.type === 'mergeVariable' || node.type === 'objectVariable' || node.type === 'templateVariable') {
      const name = node.type === 'objectVariable'
        ? `${normalizeString(node.attrs?.objectKey)}.${normalizeString(node.attrs?.propertyKey)}`
        : node.type === 'templateVariable'
          ? normalizeString(node.attrs?.key || `${node.attrs?.object || ''}.${node.attrs?.property || ''}`)
          : normalizeString(node.attrs?.name || node.attrs?.key);
      if (name) out.add(name);
    }
    if (node.type === 'text') {
      const text = String(node.text || '');
      const variablePattern = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
      let match = variablePattern.exec(text);
      while (match) {
        out.add(match[1]);
        match = variablePattern.exec(text);
      }
    }
    if (node.type === 'emailButton') {
      const attrs = node.attrs || {};
      if (attrs.hrefVariable) out.add(attrs.hrefVariable);
      collectVariablesFromString(attrs.label, out);
      collectVariablesFromString(attrs.accessibleLabel, out);
    }
    if (node.type === 'emailImage') {
      const attrs = node.attrs || {};
      ['src', 'href', 'alt', 'caption'].forEach((key) => {
        const value = String(attrs[key] || '');
        const valuePattern = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
        let match = valuePattern.exec(value);
        while (match) {
          out.add(match[1]);
          match = valuePattern.exec(value);
        }
      });
    }
  });
  return out;
}

function markToHtml(mark, data) {
  if (mark.type === 'bold') return { open: '<strong>', close: '</strong>' };
  if (mark.type === 'italic') return { open: '<em>', close: '</em>' };
  if (mark.type === 'underline') return { open: '<u>', close: '</u>' };
  if (mark.type === 'textStyle') {
    const attrs = mark.attrs || {};
    const styles = [];
    const color = normalizeString(attrs.color);
    const backgroundColor = normalizeString(attrs.backgroundColor);
    const fontFamily = normalizeString(attrs.fontFamily).replace(/[;"<>]/g, '');
    const fontSize = normalizeString(attrs.fontSize);
    if (/^#[0-9a-f]{6}$/i.test(color)) styles.push(`color:${color}`);
    if (/^#[0-9a-f]{6}$/i.test(backgroundColor)) styles.push(`background-color:${backgroundColor}`);
    if (fontFamily) styles.push(`font-family:${fontFamily}`);
    if (/^\d+(px|pt|em|rem)$/i.test(fontSize)) styles.push(`font-size:${fontSize}`);
    if (styles.length === 0) return { open: '', close: '' };
    return { open: `<span style="${escapeHtml(styles.join(';'))}">`, close: '</span>' };
  }
  if (mark.type === 'link' && isSafeUrl(replaceVariables(mark.attrs?.href || '', data))) {
    return {
      open: `<a href="${escapeHtml(replaceVariables(mark.attrs.href, data))}" rel="noopener noreferrer">`,
      close: '</a>',
    };
  }
  return { open: '', close: '' };
}

function renderInlineHtml(node, data) {
  if (!node) return '';
  if (node.type === 'text') {
    const renderedText = escapeHtml(replaceVariables(node.text || '', data));
    const marks = Array.isArray(node.marks) ? node.marks.map((mark) => markToHtml(mark, data)) : [];
    const open = marks.map((mark) => mark.open).join('');
    const close = marks.slice().reverse().map((mark) => mark.close).join('');
    return `${open}${renderedText}${close}`;
  }
  if (node.type === 'mergeVariable' || node.type === 'objectVariable' || node.type === 'templateVariable') {
    const path = node.type === 'objectVariable'
      ? `${normalizeString(node.attrs?.objectKey)}.${normalizeString(node.attrs?.propertyKey)}`
      : node.type === 'templateVariable'
        ? normalizeString(node.attrs?.key || `${node.attrs?.object || ''}.${node.attrs?.property || ''}`)
        : normalizeString(node.attrs?.name || node.attrs?.key);
    const value = getPathValue(data, path);
    return escapeHtml(value === undefined || value === null ? '' : value);
  }
  if (Array.isArray(node.content)) return node.content.map((child) => renderInlineHtml(child, data)).join('');
  return '';
}

function spacingValue(size) {
  return { small: 12, medium: 20, large: 32 }[size] || 20;
}

function safeHexColor(value, fallback = '#1d4ed8') {
  const color = normalizeString(value);
  return /^#[0-9a-f]{6}$/i.test(color) ? color : fallback;
}

function buttonStyles(style, width, size, primaryColor) {
  const color = safeHexColor(primaryColor);
  const padding = { small: '9px 14px', medium: '12px 18px', large: '14px 24px' }[size] || '12px 18px';
  const fontSize = { small: '13px', medium: '15px', large: '17px' }[size] || '15px';
  const base = `display:inline-block;text-decoration:none;border-radius:999px;font-weight:700;font-family:Arial,sans-serif;font-size:${fontSize};line-height:1.2;padding:${padding};`;
  const widthStyle = width === 'full' ? 'display:block;width:100%;box-sizing:border-box;text-align:center;' : '';
  if (style === 'secondary') return `${base}${widthStyle}background:#f1f5f9;color:#0f172a;border:1px solid #cbd5e1;`;
  if (style === 'outline') return `${base}${widthStyle}background:#ffffff;color:${color};border:1px solid ${color};`;
  if (style === 'text') return `${base}${widthStyle}background:transparent;color:${color};border:1px solid transparent;padding-left:0;padding-right:0;`;
  return `${base}${widthStyle}background:${color};color:#ffffff;border:1px solid ${color};`;
}

function resolveDestination(attrs, data) {
  const normalized = normalizeEmailButtonAttrs(attrs);
  if (normalized.destinationType === 'VARIABLE') {
    const value = getPathValue(data, normalized.hrefVariable);
    return normalizeString(value);
  }
  if (normalized.destinationType === 'EMAIL') {
    const email = normalizeEmailAddress(replaceVariables(normalized.href, data));
    return email ? `mailto:${email}` : '';
  }
  if (normalized.destinationType === 'PHONE') {
    const phone = normalizePhoneNumber(replaceVariables(normalized.href, data));
    return phone ? `tel:${phone.replace(/[\s().-]/g, '')}` : '';
  }
  return replaceVariables(normalized.href || '', data);
}

function emailButtonIcon(icon) {
  return {
    'arrow-right': '→',
    'external-link': '↗',
    download: '↓',
    calendar: '📅',
    phone: '☎',
    email: '✉',
  }[icon] || '';
}

function renderEmailButtonLabel(label, attrs) {
  const icon = emailButtonIcon(attrs.icon);
  if (!icon) return label;
  return attrs.iconPosition === 'before' ? `${icon} ${label}` : `${label} ${icon}`;
}

function renderEmailButton(node, data) {
  const attrs = normalizeEmailButtonAttrs(node.attrs || {});
  const renderedLabel = replaceVariables(attrs.label || 'Open', data);
  const label = escapeHtml(renderEmailButtonLabel(renderedLabel, attrs));
  const href = resolveDestination(attrs, data);
  const safeHref = isSafeUrl(href) ? href : '#';
  const accessibleLabel = normalizeString(replaceVariables(attrs.accessibleLabel, data));
  const primaryColor = getPathValue(data, 'tenant.primaryColor') || getPathValue(data, 'branding.primaryColor') || '#1d4ed8';
  const ariaLabel = accessibleLabel ? ` aria-label="${escapeHtml(accessibleLabel)}"` : '';
  const trackingLabel = attrs.trackingLabel ? ` data-tracking-label="${escapeHtml(attrs.trackingLabel)}"` : '';
  return [
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:16px 0;"><tr><td align="${escapeHtml(attrs.alignment)}">`,
    `<a href="${escapeHtml(safeHref)}" rel="noopener noreferrer"${ariaLabel}${trackingLabel} style="${escapeHtml(buttonStyles(attrs.style, attrs.width, attrs.size, primaryColor))}">${label}</a>`,
    '</td></tr></table>',
  ].join('');
}

function renderEmailImage(node, data) {
  const attrs = node.attrs || {};
  const src = replaceVariables(attrs.src || '', data);
  if (!isSafeUrl(src)) return '';
  const href = replaceVariables(attrs.href || '', data);
  const safeHref = href && isSafeUrl(href) ? href : '';
  const alignment = EMAIL_ALIGNMENTS.includes(attrs.alignment) ? attrs.alignment : 'center';
  const widthPreset = EMAIL_IMAGE_WIDTH_PRESETS.includes(attrs.widthPreset) ? attrs.widthPreset : 'full';
  const maxWidth = { small: 180, medium: 320, large: 480, full: 600 }[widthPreset];
  const alt = escapeHtml(replaceVariables(attrs.alt || '', data));
  const caption = normalizeString(replaceVariables(attrs.caption || '', data));
  const image = `<img src="${escapeHtml(src)}" alt="${alt}" style="display:block;border:0;max-width:${maxWidth}px;width:100%;height:auto;">`;
  const linked = safeHref ? `<a href="${escapeHtml(safeHref)}" rel="noopener noreferrer">${image}</a>` : image;
  return [
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:16px 0;"><tr><td align="${escapeHtml(alignment)}">`,
    linked,
    caption ? `<div style="color:#64748b;font-size:12px;line-height:1.4;margin-top:8px;">${escapeHtml(caption)}</div>` : '',
    '</td></tr></table>',
  ].join('');
}

function renderEmailDivider(node) {
  const attrs = node.attrs || {};
  const style = EMAIL_DIVIDER_STYLES.includes(attrs.style) ? attrs.style : 'standard';
  const spacing = spacingValue(attrs.spacing || 'medium');
  const color = style === 'subtle' ? '#e2e8f0' : '#cbd5e1';
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:${spacing}px 0;"><tr><td style="border-top:1px solid ${color};font-size:1px;line-height:1px;">&nbsp;</td></tr></table>`;
}

function renderEmailSpacer(node) {
  const size = EMAIL_SPACINGS.includes(node.attrs?.size) ? node.attrs.size : 'medium';
  const height = spacingValue(size);
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="height:${height}px;line-height:${height}px;font-size:1px;">&nbsp;</td></tr></table>`;
}

function renderEmailCallout(node, data) {
  const tone = EMAIL_CALLOUT_TONES.includes(node.attrs?.tone) ? node.attrs.tone : 'neutral';
  const tones = {
    neutral: { bg: '#f8fafc', border: '#cbd5e1', color: '#0f172a' },
    info: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e3a8a' },
    success: { bg: '#ecfdf5', border: '#a7f3d0', color: '#065f46' },
    warning: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
  };
  const palette = tones[tone] || tones.neutral;
  const inner = (node.content || []).map((child) => renderBlockHtml(child, data)).join('');
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:16px 0;"><tr><td style="background:${palette.bg};border:1px solid ${palette.border};border-radius:14px;color:${palette.color};padding:14px 16px;">${inner}</td></tr></table>`;
}

function renderBlockHtml(node, data = {}) {
  if (!node) return '';
  const inner = Array.isArray(node.content) ? node.content.map((child) => renderInlineHtml(child, data)).join('') : '';
  switch (node.type) {
    case 'doc':
      return (node.content || []).map((child) => renderBlockHtml(child, data)).join('');
    case 'paragraph':
      return `<p>${inner}</p>`;
    case 'heading': {
      const level = ALLOWED_HEADING_LEVELS.includes(Number(node.attrs?.level)) ? Number(node.attrs.level) : 2;
      return `<h${level}>${inner}</h${level}>`;
    }
    case 'bulletList':
      return `<ul>${(node.content || []).map((child) => renderBlockHtml(child, data)).join('')}</ul>`;
    case 'orderedList':
      return `<ol>${(node.content || []).map((child) => renderBlockHtml(child, data)).join('')}</ol>`;
    case 'listItem':
      return `<li>${(node.content || []).map((child) => renderBlockHtml(child, data)).join('')}</li>`;
    case 'emailButton':
      return renderEmailButton(node, data);
    case 'emailImage':
      return renderEmailImage(node, data);
    case 'emailDivider':
      return renderEmailDivider(node, data);
    case 'emailSpacer':
      return renderEmailSpacer(node, data);
    case 'emailCallout':
      return renderEmailCallout(node, data);
    default:
      return Array.isArray(node.content) ? node.content.map((child) => renderBlockHtml(child, data)).join('') : inner;
  }
}

function renderEmailEditorDocumentToHtml(document, data = {}) {
  return renderBlockHtml(document, data);
}

function renderEmailEditorNodeToText(node, data = {}) {
  if (!node) return '';
  if (node.type === 'text') return replaceVariables(node.text || '', data);
  if (node.type === 'mergeVariable' || node.type === 'objectVariable' || node.type === 'templateVariable') {
    const path = node.type === 'objectVariable'
      ? `${normalizeString(node.attrs?.objectKey)}.${normalizeString(node.attrs?.propertyKey)}`
      : node.type === 'templateVariable'
        ? normalizeString(node.attrs?.key || `${node.attrs?.object || ''}.${node.attrs?.property || ''}`)
        : normalizeString(node.attrs?.name || node.attrs?.key);
    const value = getPathValue(data, path);
    return value === undefined || value === null ? '' : String(value);
  }
  if (node.type === 'emailButton') {
    const attrs = normalizeEmailButtonAttrs(node.attrs || {});
    const label = replaceVariables(attrs.label || 'Open', data);
    const href = resolveDestination(attrs, data);
    return `${label}${href ? `: ${href}` : ''}\n`;
  }
  if (node.type === 'emailImage') {
    const attrs = node.attrs || {};
    const alt = replaceVariables(attrs.alt || 'Image', data);
    const caption = replaceVariables(attrs.caption || '', data);
    return `[Image: ${alt}]${caption ? ` ${caption}` : ''}\n`;
  }
  if (node.type === 'emailDivider') return '---\n';
  if (node.type === 'emailSpacer') return '\n';
  const childText = Array.isArray(node.content) ? node.content.map((child) => renderEmailEditorNodeToText(child, data)).join('') : '';
  if (['paragraph', 'heading', 'listItem', 'emailCallout'].includes(node.type)) return `${childText.trim()}\n`;
  return childText;
}

function renderEmailEditorDocumentToText(document, data = {}) {
  return renderEmailEditorNodeToText(document, data).replace(/\n{3,}/g, '\n\n').trim();
}

function toolbarGroupsFor(features = {}) {
  const groups = [
    { key: 'text', label: 'Text', controls: ['blockStyle', 'bold', 'italic', 'underline', 'link', 'alignment'] },
    { key: 'lists', label: 'Lists', controls: ['bulletList', 'orderedList'] },
    { key: 'insert', label: 'Insert', controls: ['emailButton', 'emailImage', 'emailDivider', 'emailSpacer', 'emailCallout'] },
    { key: 'dynamic', label: 'Dynamic', controls: ['mergeVariable'] },
  ];
  if (features.enableDeveloperTools) groups.push({ key: 'developer', label: 'Developer', controls: ['showJson', 'copyJson', 'resetBody'] });
  return groups;
}

function createEmailEditorProfile(options = {}) {
  const mode = EMAIL_EDITOR_MODE_CONFIG[options.mode] ? options.mode : EMAIL_EDITOR_MODES.PLAYGROUND;
  const features = { ...EMAIL_EDITOR_MODE_CONFIG[mode], ...(options.features || {}) };
  const allowedVariables = normalizeVariables(options.allowedVariables);
  const allowedNodeTypes = ALLOWED_NODE_TYPES.filter((type) => {
    if (type === 'emailImage') return features.allowImages !== false;
    if (type === 'emailButton') return features.allowButtons !== false;
    return true;
  });
  return {
    key: EMAIL_EDITOR_PROFILE_KEY,
    name: EMAIL_EDITOR_PROFILE_NAME,
    mode,
    supportedModes: Object.keys(EMAIL_EDITOR_MODE_CONFIG),
    readOnly: options.readOnly === true,
    features,
    allowedVariables,
    allowedNodeTypes,
    allowedMarkTypes: [...ALLOWED_MARK_TYPES],
    allowedHeadingLevels: [...ALLOWED_HEADING_LEVELS],
    extensions: allowedNodeTypes.concat(ALLOWED_MARK_TYPES),
    toolbarGroups: toolbarGroupsFor(features),
    commands: [
      'setParagraph',
      'toggleHeading1',
      'toggleHeading2',
      'toggleBold',
      'toggleItalic',
      'toggleUnderline',
      'setLink',
      'toggleBulletList',
      'toggleOrderedList',
      'insertMergeVariable',
      'insertObjectVariable',
      'insertTemplateVariable',
      'insertEmailButton',
      'insertEmailImage',
      'insertEmailDivider',
      'insertEmailSpacer',
      'insertEmailCallout',
    ],
    serialization: {
      bodyOnly: true,
      deterministicCustomNodes: ['emailButton', 'emailImage', 'emailDivider', 'emailSpacer', 'emailCallout', 'mergeVariable', 'objectVariable', 'templateVariable'],
      staticRenderer: 'renderEmailEditorDocumentToHtml',
      plainTextRenderer: 'renderEmailEditorDocumentToText',
    },
    validationRules: [
      'allowed-nodes',
      'allowed-marks',
      'h1-h2-only',
      'safe-links',
      'approved-merge-variables',
      'controlled-email-node-enums',
      'image-alt-warning',
      'document-size',
      'no-arbitrary-html',
    ],
  };
}

export {
  ALLOWED_HEADING_LEVELS,
  ALLOWED_MARK_TYPES,
  ALLOWED_NODE_TYPES,
  DEFAULT_EMAIL_EDITOR_BODY_DOC,
  DEFAULT_EMAIL_EDITOR_VARIABLES,
  EMAIL_ALIGNMENTS,
  EMAIL_BUTTON_DESTINATION_TYPES,
  EMAIL_BUTTON_ICON_POSITIONS,
  EMAIL_BUTTON_ICONS,
  EMAIL_BUTTON_SIZES,
  EMAIL_BUTTON_STYLES,
  EMAIL_CALLOUT_TONES,
  EMAIL_DIVIDER_STYLES,
  EMAIL_EDITOR_MODE_CONFIG,
  EMAIL_EDITOR_MODES,
  EMAIL_EDITOR_PROFILE_KEY,
  EMAIL_EDITOR_PROFILE_NAME,
  EMAIL_IMAGE_WIDTH_PRESETS,
  EMAIL_SPACINGS,
  EMAIL_WIDTHS,
  clone,
  collectEmailEditorVariables,
  createEmailEditorProfile,
  isSafeUrl,
  normalizeEmailButtonAttrs,
  normalizeVariables,
  renderEmailEditorDocumentToHtml,
  renderEmailEditorDocumentToText,
  renderEmailEditorNodeToText,
  validateEmailEditorDocument,
};

export default {
  ALLOWED_HEADING_LEVELS,
  ALLOWED_MARK_TYPES,
  ALLOWED_NODE_TYPES,
  DEFAULT_EMAIL_EDITOR_BODY_DOC,
  DEFAULT_EMAIL_EDITOR_VARIABLES,
  EMAIL_ALIGNMENTS,
  EMAIL_BUTTON_DESTINATION_TYPES,
  EMAIL_BUTTON_ICON_POSITIONS,
  EMAIL_BUTTON_ICONS,
  EMAIL_BUTTON_SIZES,
  EMAIL_BUTTON_STYLES,
  EMAIL_CALLOUT_TONES,
  EMAIL_DIVIDER_STYLES,
  EMAIL_EDITOR_MODE_CONFIG,
  EMAIL_EDITOR_MODES,
  EMAIL_EDITOR_PROFILE_KEY,
  EMAIL_EDITOR_PROFILE_NAME,
  EMAIL_IMAGE_WIDTH_PRESETS,
  EMAIL_SPACINGS,
  EMAIL_WIDTHS,
  clone,
  collectEmailEditorVariables,
  createEmailEditorProfile,
  isSafeUrl,
  normalizeEmailButtonAttrs,
  normalizeVariables,
  renderEmailEditorDocumentToHtml,
  renderEmailEditorDocumentToText,
  renderEmailEditorNodeToText,
  validateEmailEditorDocument,
};
